import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./checkout-page-style.css";
import InvoiceSection, {
  defaultInvoiceValue,
  validateInvoice,
  buildInvoicePayload,
} from "../../custom/invoice-section/invoice-section";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import AuthService from "../../../service/auth-service";
import PaymentService from "../../../service/payment-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";

//全站購買流程的統一結帳頁
//  流程：source 頁 → navigate("/checkout", { state }) → 選發票 → 送出 → 綠界新分頁
//  location.state 必帶：{ planID, itemName, amount, returnPath, payload? }
//    planID     - point_* 或 digital_*
//    itemName   - 顯示用商品名稱
//    amount     - 顯示用金額（NT$）
//    returnPath - 送出後導回哪一頁，該頁負責 pendingOrderID polling
//    payload    - digital_* 才有；帶去建 Order
const CheckoutPageComponent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  //從 source page 帶過來的商品資訊（缺 = 直接踢回首頁）
  const cart = location.state || {};
  const { planID, itemName, amount, payload, returnPath } = cart;

  //兌換碼只對 digital_* 開放（point_ 儲值不吃）
  const supportsCoupon = typeof planID === "string" && planID.startsWith("digital_");

  const [invoice, setInvoice] = useState(() =>
    defaultInvoiceValue(AuthService.getUserProfile()?.userEmail || ""),
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  //兌換碼狀態
  //  couponApplied: null | { code, discountAmount } — 套用成功後鎖住
  //  couponError: 錯誤訊息（無效 / 已用完 / 此商品不適用）
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);

  const discount = couponApplied ? couponApplied.discountAmount : 0;
  const finalAmount = Math.max(0, (amount ?? 0) - discount);

  //量測 sticky nav 高度，作為 mobile-main 的 padding-top（同其他 enter 頁 pattern）
  //  避免 sticky nav 覆蓋到「結帳頁」標題
  const [navHeight, setNavHeight] = useState(0);
  useEffect(() => {
    const nav = document.querySelector(
      isMobile ? ".mobile-dashboard nav" : ".dashboard nav",
    );
    setNavHeight(nav ? nav.offsetHeight : 0);
  }, [isMobile]);

  //Auth gating（雙層，同其他頁 pattern）
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  const registerFrom = "/"; //註冊完回首頁；/checkout 需要 state 沒辦法直接回來

  //初始化：檢查 state 齊全 + auth
  useEffect(() => {
    if (!planID || !itemName || amount == null) {
      navigate("/", { replace: true });
      return;
    }
    const isLogin = AuthService.getLoginStatus();
    const profile = AuthService.getUserProfile();
    if (!isLogin || !profile) {
      setShowLoginDialog(true);
    } else if (profile.userValidCode !== "2") {
      setShowRegisterPrompt(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        }
      }
    } catch (e) {
      console.error("[Checkout] firebase-login 失敗:", e);
    }
  };

  //兌換碼：套用
  const handleApplyCoupon = async () => {
    if (couponChecking) return;
    const code = couponInput.trim();
    if (!code) {
      setCouponError("請輸入兌換碼");
      return;
    }
    setCouponChecking(true);
    setCouponError("");
    try {
      const res = await PaymentService.validateCoupon(code, planID);
      const data = res.data?.data;
      if (data?.valid) {
        //前端再算一次；若折抵後 <=0 顯示不適用（後端也會擋，但這裡先給即時 feedback）
        if ((amount ?? 0) - data.discountAmount <= 0) {
          setCouponApplied(null);
          setCouponError("此商品不適用此兌換碼");
        } else {
          setCouponApplied({
            code: data.code,
            discountAmount: data.discountAmount,
          });
          setCouponError("");
        }
      } else {
        setCouponApplied(null);
        setCouponError(res.data?.message || "兌換碼無效");
      }
    } catch (e) {
      console.error("[Checkout] validate-coupon 失敗：", e);
      setCouponError("兌換碼驗證失敗，請稍後再試");
    } finally {
      setCouponChecking(false);
    }
  };

  //兌換碼：移除（改用其他碼或不用）
  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponInput("");
    setCouponError("");
  };

  //按「送出訂單」→ 建訂單 → 跳綠界新分頁 → 導回 returnPath 讓 source 頁 poll
  const handleSubmit = async () => {
    if (submitting) return;

    //auth 再檢一次（避免 dialog 被略過）
    if (!AuthService.getLoginStatus()) {
      setShowLoginDialog(true);
      return;
    }
    if (AuthService.getUserProfile()?.userValidCode !== "2") {
      setShowRegisterPrompt(true);
      return;
    }

    const check = validateInvoice(invoice);
    if (!check.valid) {
      setErrorMsg(check.message);
      return;
    }
    setErrorMsg("");
    setSubmitting(true);

    try {
      const invoicePayload = buildInvoicePayload(invoice);
      const res = planID.startsWith("point_")
        ? await PaymentService.createOrder(planID, invoicePayload)
        : await PaymentService.createDigitalOrder(
            planID,
            payload || {},
            invoicePayload,
            couponApplied?.code || "",
          );

      if (!res.data?.status) {
        setErrorMsg(res.data?.message || "建立訂單失敗，請稍後再試");
        setSubmitting(false);
        return;
      }

      //兌換碼被拒（碼失效 / 已用完 / 不適用）：清掉套用狀態，讓使用者重試
      const resultCode = res.data?.data?.resultCode;
      if (typeof resultCode === "string" && resultCode.startsWith("coupon_")) {
        setCouponApplied(null);
        setCouponError(res.data?.message || "兌換碼已失效");
        setSubmitting(false);
        return;
      }

      const { orderID, paymentParams, paymentURL } = res.data.data;

      //動態組 form 送綠界
      //  Desktop：新分頁開綠界、原分頁導回 source 頁做 pendingOrderID polling
      //  Mobile ：同分頁跳綠界（避開 Safari popup blocker — user gesture 中斷後
      //           target="_blank" 一律被封鎖）；付款完由 ECPay ClientBackURL 導回 /study-room
      const form = document.createElement("form");
      form.method = "POST";
      form.action = paymentURL;
      if (!isMobile) form.target = "_blank";
      Object.entries(paymentParams).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();

      if (!isMobile) {
        document.body.removeChild(form);
        //導回 source 頁；帶 pendingOrderID 讓那頁的 visibilitychange 開始 poll
        navigate(returnPath || "/", {
          state: { pendingOrderID: orderID, planID },
          replace: true,
        });
      }
      //Mobile 走 same-tab：form.submit() 已接管當前分頁，不 navigate、不移除 form
    } catch (e) {
      console.error("[Checkout] 建立訂單失敗：", e);
      setErrorMsg("建立訂單失敗，請稍後再試");
      setSubmitting(false);
    }
  };

  //state 沒帶東西 → useEffect 會導走，先渲染空 loading 避免 flash
  if (!planID || !itemName || amount == null) {
    return <Loading />;
  }

  const renderCouponSection = () => {
    if (!supportsCoupon) return null;
    return (
      <div className="form-card coupon-card">
        <p className="card-title">兌換碼</p>
        {!couponApplied ? (
          <div className="coupon-input-row">
            <input
              className="coupon-input"
              type="text"
              placeholder="請輸入兌換碼"
              value={couponInput}
              onChange={(e) => {
                setCouponInput(e.target.value);
                if (couponError) setCouponError("");
              }}
              disabled={couponChecking}
              maxLength={40}
            />
            <button
              className={`coupon-apply-btn${couponChecking ? " disabled" : ""}`}
              type="button"
              onClick={handleApplyCoupon}
              disabled={couponChecking}
            >
              {couponChecking ? "驗證中" : "套用"}
            </button>
          </div>
        ) : (
          <div className="coupon-applied-row">
            <div className="coupon-applied-info">
              <span className="coupon-code">{couponApplied.code}</span>
              <span className="coupon-discount">
                -NT$ {couponApplied.discountAmount}
              </span>
            </div>
            <button
              className="coupon-remove-btn"
              type="button"
              onClick={handleRemoveCoupon}
            >
              移除
            </button>
          </div>
        )}
        {couponError && <p className="coupon-error">{couponError}</p>}
      </div>
    );
  };

  const renderOrderSummary = () => (
    <div className="order-summary">
      <p className="summary-title">付款明細</p>
      <div className="summary-row">
        <span>{itemName}</span>
        <span>NT$ {amount}</span>
      </div>
      {couponApplied && (
        <div className="summary-row discount">
          <span>兌換碼 {couponApplied.code}</span>
          <span>-NT$ {couponApplied.discountAmount}</span>
        </div>
      )}
      <div className="summary-divider" />
      <div className="summary-row total">
        <span>統計</span>
        <span className="amount">NT$ {finalAmount}</span>
      </div>
    </div>
  );

  const renderMainForm = () => {
    const profile = AuthService.getUserProfile();
    return (
      <div className="main-form">
        <div className="form-card">
          <p className="card-title">
            訂購人資訊 <span className="required">*</span>
          </p>
          <div className="orderer-fields">
            <div className="field">
              <label>暱稱</label>
              <input
                className="orderer-input"
                type="text"
                readOnly
                value={profile?.userName || ""}
              />
            </div>
            <div className="field">
              <label>Email</label>
              <input
                className="orderer-input"
                type="email"
                readOnly
                value={profile?.userEmail || ""}
              />
            </div>
          </div>
        </div>

        {renderCouponSection()}

      <div className="form-card">
        <p className="card-title">
          發票資料 <span className="required">*</span>
        </p>
        <InvoiceSection
          value={invoice}
          onChange={(next) => {
            setInvoice(next);
            if (errorMsg) setErrorMsg("");
          }}
          defaultEmail={AuthService.getUserProfile()?.userEmail || ""}
        />
      </div>

        {errorMsg && <p className="error-msg">{errorMsg}</p>}
      </div>
    );
  };

  return (
    <div className="checkout-page">
      {submitting && <Loading />}

      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="checkout-content">
            <p className="page-title">結帳頁</p>
            <div className="two-column">
              <div className="left-column">{renderMainForm()}</div>
              <div className="right-column">
                {renderOrderSummary()}
                <div className="footer-actions">
                  <div className="total-row">
                    <span>訂購總計</span>
                    <span className="amount">NT$ {finalAmount}</span>
                  </div>
                  <button
                    className={`submit-btn${submitting ? " disabled" : ""}`}
                    disabled={submitting}
                    onClick={handleSubmit}
                  >
                    送出訂單
                  </button>
                </div>
              </div>
            </div>
          </div>
          <SiteFooter />
        </div>
      )}

      {isMobile && (
        <div className="mobile-main" style={{ paddingTop: navHeight + 20 }}>
          <div className="checkout-content">
            <p className="page-title">結帳頁</p>

            {/* 順序：訂購人資訊 → 兌換碼 → 發票資料 → 付款明細 → 送出訂單 → SiteFooter */}
            {renderMainForm()}

            {renderOrderSummary()}

            <div className="submit-block">
              <div className="total-row">
                <span>訂購總計</span>
                <span className="amount">NT$ {finalAmount}</span>
              </div>
              <button
                className={`submit-btn${submitting ? " disabled" : ""}`}
                disabled={submitting}
                onClick={handleSubmit}
              >
                送出訂單
              </button>
            </div>
          </div>
          <SiteFooter />
        </div>
      )}

      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
            navigate("/", { replace: true });
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
          navigate("/", { replace: true });
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="結帳前需完成註冊，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default CheckoutPageComponent;
